// ─── Station 5 send gate (Issue #802) ───────────────────────────────────
// Two distinct questions, deliberately separated:
//
//   canOpenConfirm — may the office OPEN the last-look dialog?
//   canSend        — may the packet actually GO?
//
// They were once one boolean, and that deadlocked the station. The #756
// estimate-gap acknowledgment ("I see this — send the packet anyway") is a
// checkbox that lives INSIDE the confirm dialog, so gating the dialog-opening
// button on it meant: gap present → button disabled → dialog never opens →
// the checkbox is unreachable → the tick can never happen → the job can never
// be sent. The button carries `disabled:pointer-events-none`, so the click was
// swallowed with no error and the office just saw a dead button on a job whose
// every station read green.
//
// The gap check is a SOFT gate by design — `deriveEstimateGaps`
// (src/server/routes/closeOutWorkspace.ts) does fuzzy whitespace-token
// matching between the tech's failure wording and the estimate's line
// descriptions, and its own comment says "never a hard block (it can
// false-positive on wording)". It false-positives readily in practice: test
// prose ("CV1 leaked at no reading (need >= 1 psid, closed tight)") shares no
// vocabulary with quote prose ("Labor to remove and cap …, Permit Fee"), and
// punctuation stays glued to the tokens (`psid,`, `tight)`), so those can
// never match anything. The office must still SEE and ACKNOWLEDGE the gap —
// it just must never be trapped by it.

export interface SendGateInput {
    /** Recipients with at least one document assigned. */
    planCount: number;
    /** In-play documents no recipient is set to receive. */
    missingDocCount: number;
    /** The (trimmed) email subject. */
    subject: string;
    /** A send is already in flight. */
    sending: boolean;
    /** #756 failed-device work no live estimate line appears to cover. */
    estimateGapCount: number;
    /** The office ticked "I see this — send the packet anyway". */
    gapAck: boolean;
}

export interface SendGate {
    /** The outer "Send packet" button may open the last-look dialog. */
    canOpenConfirm: boolean;
    /** The dialog's "Yes, send it now" may fire the send. */
    canSend: boolean;
}

export function deriveSendGate(input: SendGateInput): SendGate {
    const canOpenConfirm =
        input.planCount > 0 && input.missingDocCount === 0 && input.subject.trim().length > 0 && !input.sending;
    // The acknowledgment gates only the final send — never the dialog that
    // contains the checkbox (Issue #802).
    const canSend = canOpenConfirm && (input.estimateGapCount === 0 || input.gapAck);
    return { canOpenConfirm, canSend };
}
